import React from 'react';
import {
  Button,
  Checkbox,
  Input,
  Message,
  Table,
  Tooltip,
  Whisper,
  useToaster,
} from 'rsuite';
import ColumnsButton from '../../common/ColumnsButton';
import * as coursesApi from '../../api/courses';
import { SortType } from 'rsuite-table';
import EditIcon from '@rsuite/icons/Edit';
import DeleteIcon from '@rsuite/icons/Trash';
import UploadImageModal from '../../common/UploadImageModal';
import { useFormErrors } from '../../common/useFormErrors';
import { API_DOMAIN } from '../../config';
import CreateCourseModal from './modals/CreateCourseModal';
import EditCourseModal from './modals/EditCourseModal';
import CourseQueueModal from './modals/CourseQueueModal';
import PageLayout from '../../common/PageLayout';
import TablePagination from '../../common/TablePagination';
import { formatDate, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import { useTableColumns } from '../../common/useTableColumns';
import { useConfirm } from '../../common/useConfirm';
import { truncatedTextCell } from '../../common/tableCells';

const { Column, HeaderCell, Cell } = Table;

const COURSE_NAME_PREVIEW_MAX_LENGTH = 56;
const COURSE_DESCRIPTION_PREVIEW_MAX_LENGTH = 120;

const TABLE_COLUMNS = [
  { value: 'name', label: 'Назва', display: true, width: 260, minWidth: 180 },
  { value: 'cipher', label: 'Шифр', display: true, width: 90, minWidth: 80 },
  {
    value: 'requests',
    label: 'Заявок',
    display: true,
    width: 170,
    minWidth: 150,
  },
  {
    value: 'queueLength',
    label: 'Черга',
    display: true,
    width: 90,
    minWidth: 80,
  },
  {
    value: 'active',
    label: 'Статус',
    display: true,
    width: 110,
    minWidth: 100,
  },
  {
    value: 'acceptsRequests',
    label: 'Запис',
    display: true,
    width: 90,
    minWidth: 80,
  },
  {
    value: 'image',
    label: 'Зображення',
    display: true,
    width: 140,
    minWidth: 120,
  },
  {
    value: 'shortDescription',
    label: 'Короткий опис',
    display: true,
    width: 360,
    minWidth: 260,
  },
  {
    value: 'createdAt',
    label: 'Створено',
    display: false,
    width: 140,
    minWidth: 120,
  },
  {
    value: 'updatedAt',
    label: 'Оновлено',
    display: true,
    width: 140,
    minWidth: 120,
  },
];

export default function Courses() {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();

  const [courses, setCourses] = React.useState<any[]>([]);
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(15);
  const [search, setSearch] = React.useState('');

  const [sortBy, setSortBy] = React.useState('updatedAt');
  const [sortOrder, setSortOrder] = React.useState<SortType>('desc');
  const [loading, setLoading] = React.useState(false);

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    'courses:tableColumns'
  );

  const [addToQueueCourseId, setAddToQueueCourseId] =
    React.useState<string>('');
  const [updateModalObject, setUpdateModalObject] = React.useState<any | null>(
    null
  );
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);
  const [updateCourseImageObj, setUpdateCourseImageObj] =
    React.useState<null | { id?: string; image?: string }>(null);
  const {
    errorSummary: updateErrors,
    setApiError: setUpdateError,
    clearErrors: clearUpdateErrors,
  } = useFormErrors({
    fallbackMessage: 'Не вдалося оновити курс. Спробуйте пізніше',
    resetOn: [updateModalObject?.id],
  });
  const handleUpdateImageModalClose = () => {
    setUpdateCourseImageObj(null);
    getData().catch(console.error);
  };

  const clickedCellRef = React.useRef<boolean>(false);

  const getData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await coursesApi.list(sortBy, sortOrder.toUpperCase());
      setCourses(response.courses);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  const updateCourse = async () => {
    if (!updateModalObject) {
      return;
    }

    clearUpdateErrors();
    try {
      await coursesApi.update(
        updateModalObject.id,
        updateModalObject.cipher,
        updateModalObject.name,
        updateModalObject.nameEn,
        updateModalObject.nameDe,
        updateModalObject.description,
        updateModalObject.descriptionEn,
        updateModalObject.descriptionDe,
        updateModalObject.active,
        updateModalObject.acceptsRequests,
        updateModalObject.shortDescription,
        updateModalObject.shortDescriptionEn,
        updateModalObject.shortDescriptionDe,
        updateModalObject.priority
      );
      toaster.push(
        <Message type="success" showIcon closable>
          Курс оновлено
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
      setUpdateModalObject(null);
      getData().catch(console.error);
    } catch (err: any) {
      console.error(err);
      const errorSummary = setUpdateError(err);
      const errorMessage = errorSummary[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
    }
  };

  React.useEffect(() => {
    getData().catch(console.error);
  }, [getData]);

  // Search is client-side: the list endpoint returns every course in one
  // payload, so filtering in memory is instant and needs no extra request.
  const filteredCourses = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return courses;
    }
    return courses.filter(course =>
      [course.name, course.shortDescription, course.description].some(value =>
        String(value ?? '')
          .toLowerCase()
          .includes(term)
      )
    );
  }, [courses, search]);

  const totalPages = React.useMemo(() => {
    if (!filteredCourses.length) {
      return 0;
    }
    return Math.ceil(filteredCourses.length / perPage);
  }, [filteredCourses.length, perPage]);

  React.useEffect(() => {
    if (totalPages === 0) {
      if (page !== 1) {
        setPage(1);
      }
      return;
    }

    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedCourses = React.useMemo(() => {
    const safePage = page > 0 ? page : 1;
    const offset = (safePage - 1) * perPage;
    return filteredCourses.slice(offset, offset + perPage);
  }, [filteredCourses, page, perPage]);

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  const renderColumn = (column: (typeof TABLE_COLUMNS)[0]) => {
    if (column.display === false) {
      return null;
    }

    const commonProps = {
      key: column.value,
      width: column.width,
      minWidth: column.minWidth || 80,
      fixed:
        column.value === firstVisibleColumnValue
          ? ('left' as const)
          : undefined,
    };

    switch (column.value) {
      case 'name':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Назва</HeaderCell>
            <Cell dataKey="name">
              {rowData =>
                truncatedTextCell(rowData.name, {
                  maxLength: COURSE_NAME_PREVIEW_MAX_LENGTH,
                  stripTags: true,
                })
              }
            </Cell>
          </Column>
        );
      case 'cipher':
        return (
          <Column {...commonProps}>
            <HeaderCell>Шифр</HeaderCell>
            <Cell dataKey="cipher" />
          </Column>
        );
      case 'requests':
        return (
          <Column {...commonProps}>
            <HeaderCell style={{ fontSize: 12 }}>
              Заявки (ств./всього)
            </HeaderCell>
            <Cell>
              {rowData => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  {rowData.pendingRequests}/{rowData.requestsAmount}
                </span>
              )}
            </Cell>
          </Column>
        );
      case 'queueLength':
        return (
          <Column {...commonProps}>
            <HeaderCell>Черга</HeaderCell>
            <Cell dataKey="queueLength" />
          </Column>
        );
      case 'active':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Статус</HeaderCell>
            <Cell
              onClick={() => {
                clickedCellRef.current = true;
              }}
              dataKey="active"
            >
              {rowData => (
                <Checkbox
                  checked={rowData.active}
                  onChange={(_value, checked) => {
                    setCourses(prev =>
                      prev.map(c =>
                        c.id === rowData.id ? { ...c, active: checked } : c
                      )
                    );
                    coursesApi.setActive(rowData.id, checked).catch(err => {
                      console.error(err);
                      setCourses(prev =>
                        prev.map(c =>
                          c.id === rowData.id ? { ...c, active: !checked } : c
                        )
                      );
                    });
                  }}
                />
              )}
            </Cell>
          </Column>
        );
      case 'acceptsRequests':
        return (
          <Column {...commonProps}>
            <HeaderCell>Запис</HeaderCell>
            <Cell
              onClick={() => {
                clickedCellRef.current = true;
              }}
              dataKey="acceptsRequests"
            >
              {rowData => (
                <Checkbox
                  checked={rowData.acceptsRequests}
                  onChange={(_value, checked) => {
                    setCourses(prev =>
                      prev.map(c =>
                        c.id === rowData.id
                          ? { ...c, acceptsRequests: checked }
                          : c
                      )
                    );
                    coursesApi
                      .setAcceptsRequests(rowData.id, checked)
                      .catch(err => {
                        console.error(err);
                        setCourses(prev =>
                          prev.map(c =>
                            c.id === rowData.id
                              ? { ...c, acceptsRequests: !checked }
                              : c
                          )
                        );
                      });
                  }}
                />
              )}
            </Cell>
          </Column>
        );
      case 'image':
        return (
          <Column {...commonProps}>
            <HeaderCell>Зображення</HeaderCell>
            <Cell>
              {rowData => (
                <div>
                  <img
                    onClick={() => {
                      clickedCellRef.current = true;
                      setUpdateCourseImageObj({
                        id: rowData.id,
                        image: rowData.image,
                      });
                    }}
                    style={{
                      objectFit: 'contain',
                      width: '100%',
                      height: '100%',
                    }}
                    src={rowData.image}
                  />
                </div>
              )}
            </Cell>
          </Column>
        );
      case 'shortDescription':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Короткий опис</HeaderCell>
            <Cell dataKey="shortDescription" className="table-cell-description">
              {rowData =>
                truncatedTextCell(rowData.shortDescription, {
                  maxLength: COURSE_DESCRIPTION_PREVIEW_MAX_LENGTH,
                  stripTags: true,
                })
              }
            </Cell>
          </Column>
        );
      case 'createdAt':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Створено</HeaderCell>
            <Cell dataKey="createdAt">
              {rowData => formatDate(rowData.createdAt)}
            </Cell>
          </Column>
        );
      case 'updatedAt':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Оновлено</HeaderCell>
            <Cell dataKey="updatedAt">
              {rowData => formatDate(rowData.updatedAt)}
            </Cell>
          </Column>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Курси"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за назвою або описом"
              value={search}
              onChange={value => {
                setSearch(value);
                setPage(1);
              }}
            />
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
            <Button
              appearance="primary"
              onClick={() => setCreateModalOpen(true)}
            >
              Створити
            </Button>
          </>
        }
      >
        <div className="section-card">
          <Table
            className="courses-table"
            loading={loading}
            autoHeight={true}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            bordered={true}
            data={pagedCourses}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowClassName={getRowClassName}
            onRowClick={(rowData, evet) => {
              if (clickedCellRef.current) {
                clickedCellRef.current = false;
                return;
              }
              setUpdateModalObject(rowData);
            }}
            sortColumn={sortBy}
            sortType={sortOrder}
            onSortColumn={(sortColumn, sortType) => {
              setSortBy(sortColumn);
              setSortOrder(sortType || 'desc');
            }}
            wordWrap="keep-all"
          >
            {renderRowNumberColumn({ page, perPage })}

            {tableColumns.map(renderColumn)}

            <Column fixed="right" width={130}>
              <HeaderCell>Дії</HeaderCell>
              <Cell
                onClick={() => {
                  clickedCellRef.current = true;
                }}
                style={{ padding: '4px 6px' }}
              >
                {rowData => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Черга на курс</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => setAddToQueueCourseId(rowData.id)}
                      >
                        <img width={18} src="/add_to_list.svg" alt="Черга" />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Редагувати</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => setUpdateModalObject(rowData)}
                      >
                        <EditIcon />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Видалити</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        className="delete-btn"
                        onClick={async () => {
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити курс ${rowData.name}?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          try {
                            await coursesApi.remove(rowData.id);
                            toaster.push(
                              <Message type="success" showIcon closable>
                                Курс видалено
                              </Message>,
                              {
                                placement: 'topEnd',
                                duration: 1500,
                              }
                            );
                            getData()
                              .catch(console.error)
                              .then(() => setLoading(false));
                          } catch (err: any) {
                            console.error(err);
                            const errorMessage =
                              err.response?.data?.message ||
                              'Не вдалося видалити курс. Спробуйте пізніше';
                            toaster.push(
                              <Message type="error" showIcon closable>
                                {errorMessage}
                              </Message>,
                              {
                                placement: 'topEnd',
                                duration: 1500,
                              }
                            );
                          }
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>
          <TablePagination
            total={filteredCourses.length}
            activePage={page}
            pages={totalPages}
            limit={perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={limit => {
              setPerPage(limit);
              setPage(1);
            }}
            onChangePage={setPage}
          />
        </div>

        <CourseQueueModal
          courseId={addToQueueCourseId}
          setCourseId={setAddToQueueCourseId}
          callback={() => {
            getData()
              .catch(console.error)
              .then(() => setLoading(false));
          }}
        />
        <CreateCourseModal
          open={createModalOpen}
          setOpen={setCreateModalOpen}
          callback={() => {
            getData()
              .catch(console.error)
              .then(() => setLoading(false));
          }}
        />
        <EditCourseModal
          modalCourseObject={updateModalObject}
          setModalCourseObject={setUpdateModalObject}
          handleModalClose={() => {
            setUpdateModalObject(null);
          }}
          submit={updateCourse}
          errorSummary={updateErrors}
        />

        <UploadImageModal
          title="Редагувати зображення курсу"
          actionUrl={`${API_DOMAIN}/api/courses/${updateCourseImageObj?.id}/image`}
          object={updateCourseImageObj}
          handleModalClose={handleUpdateImageModalClose}
        />
      </PageLayout>
    </>
  );
}
